import { useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { useScope } from "@state/scopeContext.lib";
import { uploadDocument } from "@lib/financialDocument";
import { toBase64 } from "@helpers/file";
import { AccrualImporterContext } from "./accrualImporter.context";

interface MutationParams {
    file: File;
    documentId: string;
}

interface UseAccrualImporterUploadMutationOptions {
    onSuccess: () => void;
    onUploadError: (file: File) => void;
}

export function useAccrualImporterUploadMutation({
    onSuccess,
    onUploadError,
}: UseAccrualImporterUploadMutationOptions) {
    const { updateDocumentRow, removeDocumentRow, importerQueue } = useContext(
        AccrualImporterContext,
    );
    const { workspaceId: activeWorkspaceId } = useScope();

    return useMutation({
        mutationFn: async ({ file }: MutationParams) => {
            if (!activeWorkspaceId) {
                throw new Error("No active workspace");
            }

            return await uploadDocument({
                workspaceId: activeWorkspaceId,
                file: await toBase64(file),
                filename: file.name,
                contentType: file.type,
                importerQueue,
            });
        },
        onSuccess: (financialDocument, { documentId }) => {
            updateDocumentRow(documentId, {
                financialDocument,
                file: undefined,
            });

            onSuccess();
        },
        onError: (_error, { file, documentId }) => {
            removeDocumentRow({ id: documentId, file });
            onUploadError(file);
        },
    });
}
